import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const SESSION_KEY = 'hdux:chunk-load-reload';

function isLikelyChunkLoadFailure(error: unknown): boolean {
    if (error instanceof Error) {
        if (error.name === 'ChunkLoadError') return true;
        const m = error.message.toLowerCase();
        if (m.includes('failed to fetch dynamically imported module')) return true;
        if (m.includes('importing a module script failed')) return true;
        if (m.includes('error loading dynamically imported module')) return true;
        if (m.includes('loading chunk') && m.includes('failed')) return true;
        return false;
    }
    if (typeof error === 'string') {
        return /dynamically imported|loading chunk|import module/i.test(error);
    }
    return false;
}

/**
 * Runs a dynamic import (or any async loader). On a one-time recoverable chunk
 * failure (stale deploy / missing hashed asset), reloads the page so the shell
 * picks up current chunk URLs. Uses sessionStorage so we only auto-reload once
 * per tab to avoid an infinite loop if the asset is still missing.
 */
export async function runChunkImportWithReload<T>(importFn: () => Promise<T>): Promise<T> {
    try {
        const result = await importFn();
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SESSION_KEY);
        }
        return result;
    } catch (error) {
        if (!isLikelyChunkLoadFailure(error)) throw error;
        if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
            throw error;
        }
        if (sessionStorage.getItem(SESSION_KEY)) {
            sessionStorage.removeItem(SESSION_KEY);
            throw error;
        }
        sessionStorage.setItem(SESSION_KEY, '1');
        window.location.reload();
        return new Promise(() => {});
    }
}

/** Same as `React.lazy`, but triggers a one-time full reload on stale chunk load failure. */
export function lazyWithChunkReload<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
    return lazy(() => runChunkImportWithReload(factory));
}
